<template>
  <Dialog
    v-model:visible="visible"
    modal
    header="Статусы наличия → код состояния"
    :style="{ width: '70vw', height: '80vh' }"
    :maximizable="true"
    @update:visible="onDialogClose"
    class="site-status-mappings-dialog"
  >
    <div class="container">
      <div class="toolbar mb-3">
        <div class="flex justify-content-between align-items-start gap-3 flex-wrap">
          <div>
            <h2 class="text-900 mb-1">Словарь «Статус на сайте → код состояния»</h2>
            <p class="text-600 mt-1 mb-0">
              Текстовый статус от парсера превращается в число (0–4) в колонке
              «Состояние». Статус без правила молча становится <b>0</b>.
            </p>
          </div>
          <div class="flex gap-2">
            <Button
              label="Пересчитать сейчас"
              icon="pi pi-refresh"
              severity="secondary"
              :loading="isApplying"
              @click="applyAll"
              v-tooltip.bottom="'Запустить полный пересчёт состояния (учтёт весь словарь)'"
            />
            <Button
              label="Добавить правило"
              icon="pi pi-plus"
              @click="openCreateDialog()"
            />
          </div>
        </div>
      </div>

      <TabView v-model:activeIndex="activeTab" class="ssm-tabs">
        <!-- Вкладка 1: словарь -->
        <TabPanel header="Словарь">
          <DataTable
            :value="mappings"
            :loading="isLoading"
            :paginator="mappings.length > 15"
            :rows="15"
            class="p-datatable-sm"
            emptyMessage="Правил пока нет"
          >
            <Column field="raw_pattern" header="Статус / паттерн" style="min-width: 220px">
              <template #body="slotProps">
                <span class="font-semibold">{{ slotProps.data.raw_pattern }}</span>
                <span
                  v-if="slotProps.data.canonical_ru"
                  class="text-xs text-500 block"
                >{{ slotProps.data.canonical_ru }}</span>
              </template>
            </Column>

            <Column header="Тип совпадения" style="width: 180px">
              <template #body="slotProps">
                <Tag
                  :value="matchTypeLabel(slotProps.data.match_type)"
                  :severity="slotProps.data.match_type === 'prefix' ? 'info' : 'success'"
                />
              </template>
            </Column>

            <Column field="code" header="Код" style="width: 90px">
              <template #body="slotProps">
                <Tag :value="slotProps.data.code" severity="warning" />
              </template>
            </Column>

            <Column header="Действия" style="width: 130px">
              <template #body="slotProps">
                <div class="flex gap-1">
                  <Button
                    icon="pi pi-pencil"
                    size="small"
                    severity="secondary"
                    @click="openEditDialog(slotProps.data)"
                    v-tooltip.bottom="'Редактировать'"
                  />
                  <Button
                    icon="pi pi-trash"
                    size="small"
                    severity="danger"
                    @click="confirmDelete(slotProps.data)"
                    v-tooltip.bottom="'Удалить'"
                  />
                </div>
              </template>
            </Column>
          </DataTable>
        </TabPanel>

        <!-- Вкладка 2: необработанные статусы -->
        <TabPanel>
          <template #header>
            <span class="mr-2">Необработанные статусы</span>
            <Badge v-if="unmapped.length" :value="unmapped.length" severity="danger" />
          </template>

          <p class="text-600 mt-0 mb-3">
            Статусы, реально встречающиеся у товаров, которым ещё не присвоен код —
            сейчас они получают состояние <b>0</b>. Нажмите «Назначить код», чтобы
            добавить правило.
          </p>

          <DataTable
            :value="unmapped"
            :loading="isLoading"
            :paginator="unmapped.length > 15"
            :rows="15"
            class="p-datatable-sm"
            emptyMessage="Все статусы сопоставлены 🎉"
          >
            <Column field="value" header="Статус" style="min-width: 260px">
              <template #body="slotProps">
                <span class="font-semibold">{{ slotProps.data.value }}</span>
              </template>
            </Column>
            <Column field="count" header="Товаров" style="width: 120px">
              <template #body="slotProps">
                <Tag :value="String(slotProps.data.count)" severity="secondary" />
              </template>
            </Column>
            <Column header="Действия" style="width: 160px">
              <template #body="slotProps">
                <Button
                  label="Назначить код"
                  icon="pi pi-plus"
                  size="small"
                  @click="openCreateDialog(slotProps.data.value)"
                />
              </template>
            </Column>
          </DataTable>
        </TabPanel>
      </TabView>
    </div>

    <template #footer>
      <Button label="Закрыть" icon="pi pi-times" severity="secondary" @click="closeDialog" />
    </template>
  </Dialog>

  <!-- Add/Edit Dialog -->
  <Dialog
    v-model:visible="editorVisible"
    modal
    :header="editing ? 'Изменить правило' : 'Новое правило'"
    :style="{ width: '40rem' }"
    :closable="!isSaving"
  >
    <div class="p-fluid editor">
      <div class="field mb-3">
        <label class="block text-900 font-medium mb-2">Тип совпадения</label>
        <SelectButton
          v-model="form.match_type"
          :options="matchTypeOptions"
          optionLabel="label"
          optionValue="value"
          :allowEmpty="false"
        />
        <small class="text-500 block mt-2">
          <b>Точное</b> — статус совпадает целиком.
          <b>По началу строки</b> — для меняющихся хвостов, например дат:
          «Available from 28/06/2026» ловится паттерном «Available from».
        </small>
      </div>

      <div class="field mb-3">
        <label class="block text-900 font-medium mb-2">
          {{ form.match_type === 'prefix' ? 'Начало статуса (паттерн)' : 'Статус целиком' }}
        </label>
        <InputText
          v-model="form.raw_pattern"
          placeholder="Например: Last pieces available"
          class="w-full"
        />
      </div>

      <div class="field mb-3">
        <label class="block text-900 font-medium mb-2">Код состояния</label>
        <Dropdown
          v-model="form.code"
          :options="codeOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Выберите код 0–4"
          class="w-full"
        />
        <small class="text-500 block mt-2">
          Только 0–4. Признак склада живёт отдельно и словарём не управляется.
        </small>
      </div>

      <div class="field mb-2">
        <label class="block text-700 font-medium mb-2">
          Доп. поля (необязательно)
        </label>
        <div class="grid">
          <div class="col-8">
            <InputText
              v-model="form.canonical_ru"
              placeholder="Перевод (RU)"
              class="w-full"
            />
          </div>
          <div class="col-4">
            <InputText
              v-model="form.color"
              placeholder="Цвет"
              class="w-full"
            />
          </div>
        </div>
      </div>

      <div v-if="editorError" class="p-error text-sm mb-2">
        {{ editorError }}
      </div>

      <div class="hint text-xs text-500">
        <i class="pi pi-info-circle mr-1"></i>
        После сохранения состояние товаров пересчитывается в фоне (учтётся и весь
        словарь, и брендовые исключения).
      </div>
    </div>

    <template #footer>
      <Button
        label="Отмена"
        icon="pi pi-times"
        severity="secondary"
        :disabled="isSaving"
        @click="editorVisible = false"
      />
      <Button
        :label="editing ? 'Сохранить' : 'Создать'"
        icon="pi pi-check"
        :loading="isSaving"
        :disabled="!canSubmit"
        @click="submit"
      />
    </template>
  </Dialog>
</template>

<script>
import { ref, computed, watch } from 'vue'
import { useToast } from 'primevue/usetoast'
import { useConfirm } from 'primevue/useconfirm'
import Dialog from 'primevue/dialog'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Dropdown from 'primevue/dropdown'
import SelectButton from 'primevue/selectbutton'
import Tag from 'primevue/tag'
import Badge from 'primevue/badge'
import TabView from 'primevue/tabview'
import TabPanel from 'primevue/tabpanel'
import Tooltip from 'primevue/tooltip'
import apiService from '@/services/apiService'

export default {
  name: 'SiteStatusMappingsDialog',
  components: {
    Dialog,
    DataTable,
    Column,
    Button,
    InputText,
    Dropdown,
    SelectButton,
    Tag,
    Badge,
    TabView,
    TabPanel,
  },
  directives: { tooltip: Tooltip },
  props: {
    modelValue: { type: Boolean, default: false },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const toast = useToast()
    const confirm = useConfirm()

    const visible = ref(props.modelValue)
    const activeTab = ref(0)
    const mappings = ref([])
    const unmapped = ref([])
    const isLoading = ref(false)
    const isApplying = ref(false)

    const editorVisible = ref(false)
    const isSaving = ref(false)
    const editing = ref(null) // null = create, иначе объект правила
    const editorError = ref('')

    const form = ref({
      match_type: 'exact',
      raw_pattern: '',
      code: null,
      canonical_ru: '',
      color: '',
    })

    const matchTypeOptions = [
      { label: 'Точное', value: 'exact' },
      { label: 'По началу строки', value: 'prefix' },
    ]
    const codeOptions = ['0', '1', '2', '3', '4'].map((c) => ({ label: c, value: c }))

    const matchTypeLabel = (t) => (t === 'prefix' ? 'По началу строки' : 'Точное')

    const canSubmit = computed(() => {
      return (
        !!(form.value.raw_pattern || '').trim() &&
        form.value.code != null &&
        form.value.code !== '' &&
        !isSaving.value
      )
    })

    // ---------- Загрузка ----------
    const loadAll = async () => {
      isLoading.value = true
      try {
        const [rules, unmappedList] = await Promise.all([
          apiService.getSiteStatusMappings(),
          apiService.getUnmappedSiteStatuses(),
        ])
        mappings.value = rules || []
        unmapped.value = unmappedList || []
      } catch (e) {
        toast.add({
          severity: 'error',
          summary: 'Ошибка',
          detail: 'Не удалось загрузить словарь статусов',
          life: 5000,
        })
      } finally {
        isLoading.value = false
      }
    }

    // ---------- Add / Edit ----------
    const resetForm = () => {
      form.value = {
        match_type: 'exact',
        raw_pattern: '',
        code: null,
        canonical_ru: '',
        color: '',
      }
      editorError.value = ''
    }

    // value — необязательное предзаполнение паттерна (из «Необработанных»)
    const openCreateDialog = (value) => {
      editing.value = null
      resetForm()
      if (typeof value === 'string' && value) {
        form.value.raw_pattern = value
      }
      editorVisible.value = true
    }

    const openEditDialog = (rule) => {
      editing.value = rule
      form.value = {
        match_type: rule.match_type,
        raw_pattern: rule.raw_pattern || '',
        code: rule.code != null ? String(rule.code) : null,
        canonical_ru: rule.canonical_ru || '',
        color: rule.color || '',
      }
      editorError.value = ''
      editorVisible.value = true
    }

    const submit = async () => {
      if (!canSubmit.value) return
      const payload = {
        match_type: form.value.match_type,
        raw_pattern: (form.value.raw_pattern || '').trim(),
        code: String(form.value.code),
        canonical_ru: (form.value.canonical_ru || '').trim(),
        color: (form.value.color || '').trim(),
      }
      isSaving.value = true
      editorError.value = ''
      try {
        if (editing.value) {
          await apiService.updateSiteStatusMapping(editing.value.id, payload)
          toast.add({
            severity: 'success',
            summary: 'Сохранено',
            detail: 'Правило обновлено. Состояние пересчитывается в фоне.',
            life: 4000,
          })
        } else {
          await apiService.createSiteStatusMapping(payload)
          toast.add({
            severity: 'success',
            summary: 'Добавлено',
            detail: 'Правило добавлено. Состояние пересчитывается в фоне.',
            life: 4000,
          })
        }
        editorVisible.value = false
        await loadAll()
      } catch (e) {
        editorError.value =
          e?.response?.data?.detail || 'Не удалось сохранить правило'
      } finally {
        isSaving.value = false
      }
    }

    const confirmDelete = (rule) => {
      confirm.require({
        message: `Удалить правило «${rule.raw_pattern}» → ${rule.code}?`,
        header: 'Подтверждение',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Удалить',
        rejectLabel: 'Отмена',
        acceptClass: 'p-button-danger',
        accept: async () => {
          try {
            await apiService.deleteSiteStatusMapping(rule.id)
            toast.add({
              severity: 'success',
              summary: 'Удалено',
              detail: 'Запущен пересчёт состояния (обновится в течение пары минут).',
              life: 4000,
            })
            await loadAll()
          } catch (e) {
            toast.add({
              severity: 'error',
              summary: 'Ошибка',
              detail: e?.response?.data?.detail || 'Не удалось удалить правило',
              life: 5000,
            })
          }
        },
      })
    }

    const applyAll = async () => {
      isApplying.value = true
      try {
        await apiService.applySiteStatusMappings()
        toast.add({
          severity: 'success',
          summary: 'Запущено',
          detail: 'Пересчёт состояния идёт в фоне.',
          life: 4000,
        })
      } catch (e) {
        toast.add({
          severity: e?.response?.status === 409 ? 'warn' : 'error',
          summary: e?.response?.status === 409 ? 'Уже выполняется' : 'Ошибка',
          detail:
            e?.response?.data?.detail || 'Не удалось запустить пересчёт состояния',
          life: 5000,
        })
      } finally {
        isApplying.value = false
      }
    }

    // ---------- Dialog visibility ----------
    const closeDialog = () => {
      visible.value = false
    }

    const onDialogClose = (value) => {
      visible.value = value
      emit('update:modelValue', value)
    }

    watch(
      () => props.modelValue,
      (v) => {
        visible.value = v
        if (v) {
          activeTab.value = 0
          loadAll()
        }
      }
    )

    return {
      visible,
      activeTab,
      mappings,
      unmapped,
      isLoading,
      isApplying,
      editorVisible,
      isSaving,
      editing,
      editorError,
      form,
      matchTypeOptions,
      codeOptions,
      matchTypeLabel,
      canSubmit,
      openCreateDialog,
      openEditDialog,
      submit,
      confirmDelete,
      applyAll,
      closeDialog,
      onDialogClose,
    }
  },
}
</script>

<style scoped>
.container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.toolbar h2 {
  margin: 0;
  font-size: 1.1rem;
}

.ssm-tabs {
  flex: 1;
  min-height: 0;
}

.editor .field {
  display: flex;
  flex-direction: column;
}

.hint {
  padding: 8px;
  background: var(--surface-100);
  border-radius: 4px;
}
</style>
